// src/pages/LeMieCommessePage.js
// Pannello personale per il PM — le commesse di cui è responsabile, in tabella a colonne
// (non a card), con importo, fatturato, saldo da fatturare e una nota libera per commessa.
// Visibile solo se abilitato dal titolare (sezione "le-mie-commesse" in Abilitazioni).
import React, { useState, useEffect } from "react";
import { collection, query, where, onSnapshot, getDocs, doc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import NumeroOrdineCommessa from "../components/NumeroOrdineCommessa";
import { confrontaNumeroOrdine } from "../utils/numeroOrdine";
import { useAuth } from "../contexts/AuthContext";
import { useCanSee } from "../hooks/useCanSee";
import { useOrg } from "../contexts/OrgContext";
import { useToast } from "../utils/Toast";
import { ChevronLeft, Save } from "lucide-react";
import { calcolaSAL } from "../utils/calcoloSAL";
import { totaleCommessa } from "../utils/economiaCommessa";

function fmt(n) { return (parseFloat(n)||0).toLocaleString("it-IT",{minimumFractionDigits:2,maximumFractionDigits:2}); }
function dataIt(d) {
  const dt = d?.toDate ? d.toDate() : (typeof d==="string" ? new Date(d) : null);
  return dt && !isNaN(dt) ? dt.toLocaleDateString("it-IT") : "—";
}

export default function LeMieCommessePage({ onNavigate }) {
  const { currentUser, isOrgAdmin, isPlatformOwner } = useAuth();
  const { effectiveOrgId } = useOrg();
  const { addToast } = useToast();
  const canSee = useCanSee();
  const puoAccedere = isOrgAdmin || isPlatformOwner || canSee("le-mie-commesse");
  const [righe, setRighe] = useState([]);
  const [caricato, setCaricato] = useState(false);
  const [noteModificate, setNoteModificate] = useState({}); // {projectId: testo} — non ancora salvate
  const [salvando, setSalvando] = useState(null); // projectId in salvataggio

  useEffect(() => {
    if (!effectiveOrgId || !currentUser?.uid) return;
    const unsub = onSnapshot(
      query(collection(db,"projects"), where("organizationId","==",effectiveOrgId), where("responsabileUid","==",currentUser.uid)),
      async (snap) => {
        const commesse = snap.docs.map(d=>({id:d.id,...d.data()})).filter(p=>!p.isParent && !p.archived);
        // Per ognuna, il fatturato e il saldo da fatturare vengono dal Computo SAL, se esiste
        const salSnap = await getDocs(query(collection(db,"computiSAL"), where("orgId","==",effectiveOrgId)));
        const salPerProject = {};
        salSnap.docs.forEach(d => { salPerProject[d.data().projectId] = d.data(); });
        // Importo della commessa: lo stesso del Controllo Economico e della Cabina di Regia
        // (importo a contratto del budget + varianti). Prima si leggeva un campo del progetto
        // che non esiste, e la colonna restava sempre vuota.
        const budgetPerProject = {};
        try {
          const budgetSnap = await getDocs(query(collection(db,"budgetCommessa"), where("organizationId","==",effectiveOrgId)));
          // W452: con due budget vale il primo per identificativo, come nel Controllo Economico
          budgetSnap.docs.forEach(d => { const b = d.data(); if (b.projectId && !budgetPerProject[b.projectId]) budgetPerProject[b.projectId] = b; });
        } catch { /* senza accesso al budget la colonna resta vuota */ }

        const risultati = commesse.map(p => {
          const sal = salPerProject[p.id];
          let importoFatturato = null, saldoDaFatturare = null;
          if (sal && (sal.periodi||[]).length>0) {
            const dati = calcolaSAL(sal.righe||[], sal.periodi, sal.fatturato||0);
            importoFatturato = dati.fatturato;
            saldoDaFatturare = dati.daFatturare;
          }
          return {
            id: p.id,
            data: dataIt(p.createdAt),
            codiceCommessa: p.codiceCommessa||"—",
            numeroOrdine: p.numeroOrdine||"",
            clienteNome: p.clienteNome||"—",
            nome: p.name||"—",
            importo: budgetPerProject[p.id] && totaleCommessa(budgetPerProject[p.id]) > 0 ? totaleCommessa(budgetPerProject[p.id]) : null,
            importoFatturato, saldoDaFatturare,
            stato: p.statoCommessa==="chiusa" ? "Chiusa" : "Attiva",
            note: p.notaPM || "",
          };
        }).sort((a,b)=> (a.stato==="Attiva"?0:1) - (b.stato==="Attiva"?0:1) || confrontaNumeroOrdine(a.numeroOrdine, b.numeroOrdine));

        setRighe(risultati);
        setCaricato(true);
      }
    );
    return unsub;
  }, [effectiveOrgId, currentUser]);

  const salvaNota = async (projectId) => {
    const testo = noteModificate[projectId];
    if (testo === undefined) return;
    setSalvando(projectId);
    try {
      await updateDoc(doc(db,"projects",projectId), { notaPM: testo });
      addToast("Nota salvata ✅","success");
      setNoteModificate(prev => { const n={...prev}; delete n[projectId]; return n; });
    } catch (err) {
      console.error("Errore salvataggio nota PM:", err);
      addToast("Errore nel salvare la nota","error");
    } finally {
      setSalvando(null);
    }
  };

  if (!caricato) return <div style={{padding:40,textAlign:"center",color:"#9ca3af"}}>Caricamento...</div>;

  if (!puoAccedere) {
    return (
      <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",height:"100%",padding:40,textAlign:"center"}}>
        <div style={{fontSize:40,marginBottom:12}}>🔒</div>
        <div style={{fontSize:15,fontWeight:700,color:"#374151",marginBottom:6}}>Accesso riservato</div>
        <div style={{fontSize:13,color:"#9ca3af",maxWidth:360}}>Questa sezione è visibile solo a Titolare, Gestione aziendale e Project Manager.</div>
      </div>
    );
  }

  return (
    <div style={{padding:24,maxWidth:1200,overflowY:"auto",height:"100%"}}>
      <button onClick={()=>onNavigate&&onNavigate({page:"home"})} style={{background:"none",border:"none",cursor:"pointer",color:"#7c3aed",display:"flex",alignItems:"center",gap:4,fontSize:13,fontWeight:600,marginBottom:14}}>
        <ChevronLeft size={15}/> Torna alla Dashboard
      </button>

      <h2 style={{fontSize:18,fontWeight:800,color:"#1e1b4b",marginBottom:4}}>📊 Le mie commesse</h2>
      <p style={{fontSize:12,color:"#6b7280",marginBottom:18}}>Solo le commesse di cui sei responsabile — {righe.length} in elenco.</p>

      {righe.length===0 ? (
        <div style={{textAlign:"center",padding:60,color:"#9ca3af"}}>Nessuna commessa a te assegnata al momento.</div>
      ) : (
        <div style={{overflowX:"auto",background:"#fff",border:"1px solid #e5e7eb",borderRadius:12}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:12.5,minWidth:1000}}>
            <thead>
              <tr style={{background:"#1e1b4b"}}>
                {["N. ordine","Commessa","Data","Cliente","Descrizione Lavori","Importo","Importo Fatturato","Saldo da Fatturare","Stato","Note"].map(h=>(
                  <th key={h} style={{padding:"8px 10px",color:"#fff",fontSize:10,fontWeight:700,textTransform:"uppercase",textAlign:"left",whiteSpace:"nowrap"}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {righe.map((r,i) => (
                <tr key={r.id} style={{background:i%2===0?"#fff":"#fafafa",borderBottom:"1px solid #f3f4f6"}}>
                  <td style={{padding:"8px 10px",whiteSpace:"nowrap"}}>
                    <NumeroOrdineCommessa projectId={r.id} valore={r.numeroOrdine} puoModificare compatto/>
                  </td>
                  <td onClick={()=>onNavigate&&onNavigate({page:"project",projectId:r.id,project:{id:r.id,name:r.nome}})}
                    style={{padding:"8px 10px",fontWeight:700,color:"#7c3aed",whiteSpace:"nowrap",cursor:"pointer",textDecoration:"underline",textDecorationColor:"transparent",transition:"text-decoration-color 0.15s"}}
                    onMouseEnter={e=>e.currentTarget.style.textDecorationColor="#7c3aed"}
                    onMouseLeave={e=>e.currentTarget.style.textDecorationColor="transparent"}>
                    {r.codiceCommessa}
                  </td>
                  <td style={{padding:"8px 10px",color:"#6b7280",whiteSpace:"nowrap"}}>{r.data}</td>
                  <td style={{padding:"8px 10px",fontWeight:600,color:"#111827"}}>{r.clienteNome}</td>
                  <td style={{padding:"8px 10px",color:"#374151"}}>{r.nome}</td>
                  <td style={{padding:"8px 10px",color:"#374151",whiteSpace:"nowrap"}}>{r.importo!=null?`€${fmt(r.importo)}`:"—"}</td>
                  <td style={{padding:"8px 10px",color:"#059669",fontWeight:600,whiteSpace:"nowrap"}}>{r.importoFatturato!=null?`€${fmt(r.importoFatturato)}`:"—"}</td>
                  <td style={{padding:"8px 10px",color:"#d97706",fontWeight:600,whiteSpace:"nowrap"}}>{r.saldoDaFatturare!=null?`€${fmt(r.saldoDaFatturare)}`:"—"}</td>
                  <td style={{padding:"8px 10px",whiteSpace:"nowrap"}}>
                    <span style={{fontSize:10.5,fontWeight:700,padding:"2px 8px",borderRadius:10,background:r.stato==="Attiva"?"#d1fae5":"#f3f4f6",color:r.stato==="Attiva"?"#065f46":"#6b7280"}}>{r.stato}</span>
                  </td>
                  <td style={{padding:"6px 8px",minWidth:220}}>
                    <div style={{display:"flex",gap:4,alignItems:"center"}}>
                      <input
                        value={noteModificate[r.id] !== undefined ? noteModificate[r.id] : r.note}
                        onChange={e=>setNoteModificate(prev=>({...prev,[r.id]:e.target.value}))}
                        placeholder="Nota libera..."
                        style={{flex:1,padding:"5px 8px",border:"1px solid #e5e7eb",borderRadius:6,fontSize:11.5,fontFamily:"'Inter',sans-serif"}}/>
                      {noteModificate[r.id] !== undefined && noteModificate[r.id] !== r.note && (
                        <button onClick={()=>salvaNota(r.id)} disabled={salvando===r.id}
                          title="Salva nota"
                          style={{flexShrink:0,padding:"5px 7px",border:"none",borderRadius:6,background:"#7c3aed",color:"#fff",cursor:"pointer",display:"flex"}}>
                          <Save size={13}/>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
